/**
 * Everything on the public site that is DATA rather than layout: the
 * figures, the benefit cards, the steps, who the program is for, the
 * onboarding stages, what each side commits to, the FAQ, the per-page
 * titles.
 *
 * Pages render from these arrays and inline nothing, so a marketing edit
 * is an edit here and `content.test.ts` can check the things a human
 * reviewer forgets (a link to a route that does not exist, a title too long
 * for a search result, the retired product name).
 *
 * Every block that came from xopnetworks.com names its source page. All
 * were fetched on 2026-09-08. Facts that already live elsewhere are NOT
 * repeated here: the address, phone and mailboxes are in `lib/company.ts`,
 * the product name in `@xop-any-time/shared`.
 */

import { XOP_CONTACT } from "../../lib/company.js";

export const PUBLIC_ROUTES = {
  home: "/",
  program: "/reseller-program",
  login: "/login",
  register: "/register",
  terms: "/terms",
  privacy: "/privacy",
} as const;

export interface PageMeta {
  /** ≤ 60 characters; the layout appends " · XOP Reseller". */
  title: string;
  /** 40–160 characters. */
  description: string;
}

export const PAGE_META = {
  home: {
    title: "Become an XOP Networks reseller",
    description:
      "Join the XOP Networks Reseller Program to sell secure conferencing and notification systems with competitive margins and priority support.",
  },
  program: {
    title: "Reseller Program",
    description:
      "Apply in minutes, sign the agreement and NDA, get activated, then sell customers, quotes, invoices and licences in one partner portal.",
  },
  terms: {
    title: "Terms of Use",
    description: "The terms on which XOP Networks makes the XOP Reseller portal available to its resellers and channel partners.",
  },
  privacy: {
    title: "Privacy Policy",
    description: "What XOP Networks collects through the XOP Reseller portal, why, who can see it and how long it is kept.",
  },
  login: {
    title: "Sign in",
    description: "Sign in to XOP Reseller, the portal for XOP Networks resellers and channel partners.",
  },
  register: {
    title: "Apply to become a reseller",
    description:
      "Apply to become an XOP Networks reseller. No fee, no password yet — tell us who you are and we will send your next step by email.",
  },
} as const satisfies Record<string, PageMeta>;

export interface Cta {
  label: string;
  href: string;
}

/** Source: the product's own ask, plus xopnetworks.com (its home and
 * about pages). */
export const HERO = {
  headline: "Become an XOP Networks reseller.",
  lede:
    "Sell and support secure conferencing, notification and gateway systems that have run airports, trading floors, utilities and public-safety networks for over twenty years — with XOP Networks' margins, tools and people behind you.",
  primary: { label: "Get started", href: PUBLIC_ROUTES.register },
  secondary: { label: "Sign in", href: PUBLIC_ROUTES.login },
} as const satisfies { headline: string; lede: string; primary: Cta; secondary: Cta };

export interface Stat {
  figure: string;
  label: string;
}

/** Source: xopnetworks.com (home: "Founded in 2003", "20+ years"),
 * xopnetworks.com/reseller-partners ("30+ Global partners", "10+ Solutions").
 * The public page's "70% of partners report higher earnings within year
 * one" is deliberately NOT used: it is an earnings claim this portal cannot
 * substantiate. */
export const STATS: readonly Stat[] = [
  { figure: "2003", label: "Founded in Dallas, Texas" },
  { figure: "20+", label: "Years in mission-critical telecom" },
  { figure: "30+", label: "Partners worldwide" },
  { figure: "10+", label: "Solutions in the catalogue" },
];

export interface Benefit {
  title: string;
  summary: string;
}

/** Source: xopnetworks.com/reseller-partners (the four "Partner Benefits"). */
export const BENEFITS: readonly Benefit[] = [
  {
    title: "High margins",
    summary: "Competitive reseller pricing with volume-based discounts, so a growing book earns more per deal.",
  },
  {
    title: "Priority support",
    summary: "Dedicated assistance, fast issue resolution and direct access to XOP Networks' technical experts.",
  },
  {
    title: "Marketing assistance",
    summary: "Co-branding assets and campaign material designed to convert.",
  },
  {
    title: "Sales enablement",
    summary: "Product training, demos, pricing tools and technical documentation.",
  },
];

export interface Step {
  title: string;
  summary: string;
}

/** This portal's real onboarding flow (spec E, 2026-09-07). */
export const STEPS: readonly Step[] = [
  {
    title: "Apply",
    summary: "Five minutes, no password yet. Tell us who you are and where you sell.",
  },
  {
    title: "Sign the agreement and NDA",
    summary: "Download the Reseller Agreement and NDA, sign them, upload the signed copies.",
  },
  {
    title: "Get activated",
    summary: "XOP Networks countersigns and matches your account to the reseller record it already holds — or creates one.",
  },
  {
    title: "Sell and manage",
    summary: "Customers, quotes, invoices, licences and the leads XOP Networks sends you — in one portal.",
  },
];

export interface Feature {
  title: string;
  summary: string;
}

/** What already exists behind the sign-in wall (README, "Current state"). */
export const PORTAL_FEATURES: readonly Feature[] = [
  { title: "Customers & contacts", summary: "Your book of business, with every contact and every document in one place." },
  { title: "Quotes & invoices", summary: "Ask XOP Networks for a quote on any of your customers, and see every invoice it issues to you." },
  { title: "Licences you issue", summary: "Build the licence a customer may run and hand them the file that proves it." },
  { title: "Leads from XOP Networks", summary: "Prospects XOP Networks' sales team attaches to your company appear under Prospects." },
  { title: "Documentation", summary: "Data sheets, release notes and guides, curated for resellers." },
  { title: "Your team & network", summary: "Invite colleagues, and set up sub-resellers under your own account." },
];

export const PROGRAM_INTRO =
  "The program is for telecom VARs, system integrators and managed-service providers who serve aviation, energy, finance, public safety and enterprise customers — companies that already sell and support communications systems and want a secure, proven conferencing and notification line to add to them. Active partners sell XOP Networks' products under their own name, at their own price, with XOP Networks' engineers behind them.";

/* ── The Reseller Program page's own material.
 *
 * Deliberately NOT the home page in longer words: `BENEFITS`, `STEPS` and
 * `PORTAL_FEATURES` are the front door's pitch, and the program page used to
 * replay all three. What a prospect actually needs before applying is
 * whether they qualify, what the four onboarding stages ask of them (and
 * what XOP does in between), and what each side is signing up to — so that
 * is what lives here and nowhere else. */

/** Who the program is for. Source: spec E's applicant profile and
 * xopnetworks.com/reseller-partners' partner definition. */
export const ELIGIBILITY: readonly string[] = [
  "A registered business that sells or supports communications systems — telecom VARs, system integrators and managed-service providers.",
  "Technical staff who can install and support an XOP Networks system, with XOP Networks' engineers behind them for anything they cannot resolve.",
  "Customers in one or more of XOP Networks' markets: aviation, oil and gas, chemical, power generation, FinTech and trader voice, public safety, and enterprise.",
  "Someone who can sign XOP Networks' Reseller Agreement and NDA on behalf of the company.",
];

export interface OnboardingStage {
  /** Whose move this stage is — the pill on the timeline rail. */
  who: "You" | "XOP Networks";
  title: string;
  body: string;
  /** What lands in the applicant's inbox (or portal) when the stage closes;
   * rendered after a "You receive:" prefix, so it is a phrase, not a
   * sentence. */
  receive: string;
}

/** The real onboarding flow, stage by stage (spec E, 2026-09-07) — the same
 * four moments as `STEPS`, but told as a sequence with an owner and an
 * outcome rather than as four cards of benefit copy. */
export const ONBOARDING: readonly OnboardingStage[] = [
  {
    who: "You",
    title: "Apply online",
    body: "Five minutes: company, website, your name and work email, where you sell. No fee and no password yet.",
    receive: "an email with a link that sets your password and confirms the address.",
  },
  {
    who: "You",
    title: "Sign the Reseller Agreement and NDA",
    body: "Your first sign-in shows one screen: download both documents, sign them, and upload the signed PDFs. Nothing else opens until they are in.",
    receive: "a confirmation that XOP Networks has your documents.",
  },
  {
    who: "XOP Networks",
    title: "Review and countersign",
    body: "A person at XOP Networks reads the application and checks it against XOP Networks' own records. If your company already works with XOP Networks, your account is linked to the reseller record XOP Networks holds, so your history and any leads stay attached; otherwise a new record is created.",
    receive: "an email when your account is activated, or a note explaining why it was not.",
  },
  {
    who: "You",
    title: "Set up and sell",
    body: "The full portal opens: invite your team and start requesting quotes. Prospects XOP Networks' sales team attaches to your company appear under Prospects.",
    receive: "your customers, quotes, invoices, licences and the documentation in one place.",
  },
];

export interface CommitmentGroup {
  /** The side making the promises — a card heading, so a name, not a label. */
  title: string;
  items: readonly string[];
}

/** What the Reseller Agreement amounts to in plain words, both ways round.
 * XOP's column is the four published "Partner Benefits" plus the leads the
 * portal actually delivers; yours is what the agreement and the NDA ask. */
export const COMMITMENTS: readonly CommitmentGroup[] = [
  {
    title: "You",
    items: [
      "Sell and support XOP Networks systems under your own name and at your own price.",
      "Keep your company, team and contact details current in the portal.",
      "Keep XOP Networks' pricing and technical material inside your company, as the NDA sets out.",
      "Tell XOP Networks where you sell, so leads reach the right partner.",
    ],
  },
  {
    title: "XOP Networks",
    items: [
      "Reseller pricing with volume-based discounts.",
      "Priority support with direct access to the engineers who built the products.",
      "Product training, demos, pricing tools and technical documentation.",
      "Co-branded marketing material for your campaigns.",
      "Leads from XOP Networks' sales team, attached to your account.",
    ],
  },
];

export interface Faq {
  question: string;
  answer: string;
}

export const FAQ: readonly Faq[] = [
  {
    question: "Does it cost anything to join?",
    answer: "No. There is no fee to apply and no fee to hold a reseller account.",
  },
  {
    question: "How long does activation take?",
    answer:
      "Applications are reviewed by a person at XOP Networks. You get an email the moment yours moves — when your documents are received, and when your account is activated — and the portal shows where it stands whenever you sign in.",
  },
  {
    question: "We already work with XOP Networks. Do we apply again?",
    answer:
      "Yes — apply with your work email. XOP Networks matches your application to the reseller record it already holds, so your history and any leads stay attached to the same account.",
  },
  {
    question: "Can I add my colleagues?",
    answer:
      "Yes. Once your account is active, the owner invites team members from Account ▸ Team, and can set up sub-resellers of their own under Network.",
  },
  {
    question: "Where do leads come from?",
    answer:
      "XOP Networks' sales team attaches prospects to your company in its CRM. They appear under Prospects in the portal as soon as they are attached, with the contact details you need to follow up.",
  },
  {
    question: "Can I set my own prices?",
    answer:
      "Yes. XOP Networks' reseller pricing is what you pay; you quote and invoice your customers in your own name at the price you choose.",
  },
  {
    question: "Who supports the end customer?",
    answer:
      "You are your customer's first line of support. XOP Networks' engineers back you with priority support for anything you cannot resolve.",
  },
  {
    question: "Which countries does the program cover?",
    answer:
      "XOP Networks works with resellers across the United States and globally. Tell us where you sell in the application and we will take it from there.",
  },
];

export interface SiteMapColumn {
  heading: string;
  links: readonly { label: string; href: string; external?: boolean }[];
}

/** The footer's link columns: page links only. The contact details are
 * printed once by `SiteFooter`'s own strip, from `lib/company.ts` — which
 * is also where the marketing site's own address comes from, rather than a
 * second literal here. */
export const SITE_MAP: readonly SiteMapColumn[] = [
  {
    heading: "Company",
    links: [
      { label: "Reseller Program", href: PUBLIC_ROUTES.program },
      { label: "Terms of Use", href: PUBLIC_ROUTES.terms },
      { label: "Privacy Policy", href: PUBLIC_ROUTES.privacy },
      { label: XOP_CONTACT.website, href: `https://${XOP_CONTACT.website}`, external: true },
    ],
  },
  {
    heading: "Portal",
    links: [
      { label: "Sign in", href: PUBLIC_ROUTES.login },
      { label: "Apply to become a reseller", href: PUBLIC_ROUTES.register },
    ],
  },
];
